import React from 'react';
import { Typography, Box, styled } from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useEta } from '../../../hooks/display/useEta';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';

const PREFIX = 'MemberTripLegStatusCard';

const classes = {
  container: `${PREFIX}-container`,
  row: `${PREFIX}-row`,
  column: `${PREFIX}-col`,
  label: `${PREFIX}-label`,
  display: `${PREFIX}-display`,
  success: `${PREFIX}-success`,
  error: `${PREFIX}-error`,
  provider: `${PREFIX}-company`,
  infoPanel: `${PREFIX}-info-panel`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.lightActionDisabled04p.main,
    borderRadius: theme.shape.borderRadius,
    gap: theme.spacing(1),
  },
  [`& .${classes.row}`]: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(2),
  },
  [`& .${classes.column}`]: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  [`& .${classes.label}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  [`& .${classes.display}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 24,
    color: theme.palette.text.primary,
  },
  [`& .${classes.success}`]: {
    color: theme.palette.success.main,
  },
  [`& .${classes.error}`]: {
    color: theme.palette.error.main,
  },
  [`& .${classes.provider}`]: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 'normal',
  },
  [`& .${classes.infoPanel}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
  },
}));

interface MemberTripLegStatusCardProps {
  tripLegStatus?: string;
  eta?: number;
  driverName?: string;
  vehicleName?: string;
  vehicleModel?: string;
}

const MemberTripLegStatusCard: React.FC<MemberTripLegStatusCardProps> = ({
  tripLegStatus,
  eta,
  driverName,
  vehicleName,
  vehicleModel,
}) => {
  const t = useCommonTranslation();
  const formattedEta = useEta(eta);
  const displayEta = eta ? formattedEta : '--';

  const statusClasses = React.useMemo(
    () => [classes.display, ...(tripLegStatus ? [classes.success] : [])],
    [tripLegStatus],
  );

  const etaClasses = React.useMemo(
    () => [classes.display, ...(eta ? [classes.success] : [])],
    [eta],
  );

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.row}>
        <Box className={classes.column}>
          <Typography component="span" className={classes.label}>
            {t('member.status')}
          </Typography>
          <Typography component="span" className={statusClasses.join(' ')}>
            {t(`tripLegStatus.${tripLegStatus}`)}
          </Typography>
        </Box>
        <Box className={classes.column}>
          <Typography component="span" className={classes.label}>
            {t('member.eta')}
          </Typography>
          <Typography component="span" className={etaClasses.join(' ')}>
            {displayEta}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.row}>
        <Box className={classes.column}>
          <StyledBox className={classes.infoPanel}>
            <DirectionsCarFilledOutlinedIcon color="info" />
            <StyledBox>
              {driverName && (
                <Typography variant="body1">
                  <StyledBox component="span" className="bold">
                    {driverName}
                  </StyledBox>
                  {vehicleName && <StyledBox>{vehicleName}</StyledBox>}
                  {vehicleModel && (
                    <Typography variant="body2" color="text.secondary">
                      {vehicleModel}
                    </Typography>
                  )}
                </Typography>
              )}
            </StyledBox>
          </StyledBox>
        </Box>
      </Box>
    </StyledBox>
  );
};

export default MemberTripLegStatusCard;
